import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReplayIcon from "@mui/icons-material/Replay";
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailInboundService from "../../services/EmailInboundService";
import { errorMessage } from "../../shared/utils/validators";
import { formatWhen, statusColor } from "./helpers";

export default function InboundEmailEventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await EmailInboundService.getEvent(eventId);
      setEvent(data);
    } catch (err) {
      setError(errorMessage(err) || "Failed to load email");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const recreate = async () => {
    setBusy(true);
    try {
      const result = await EmailInboundService.retryEvent(eventId);
      setEvent(result?.event || result || event);
    } catch (err) {
      setError(errorMessage(err) || "Recreate failed");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    const phone = (event?.phones_found || [])[0] || event?.parsed?.mobile || "";
    if (!window.confirm(phone ? `Resend WhatsApp to ${phone}?` : "Resend WhatsApp for this email?")) return;
    setBusy(true);
    try {
      const result = await EmailInboundService.resendEvent(eventId);
      setEvent(result?.event || result || event);
    } catch (err) {
      setError(errorMessage(err) || "Resend failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this activity log entry?")) return;
    setBusy(true);
    try {
      await EmailInboundService.deleteEvent(eventId);
      navigate("/inbound-email");
    } catch (err) {
      setError(errorMessage(err) || "Delete failed");
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!event) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/inbound-email")}>Back</Button>
        <Alert severity="error" sx={{ mt: 2 }}>{error || "Email not found"}</Alert>
      </Box>
    );
  }

  const parsed = event.parsed || {};
  const phones = event.phones_found || [];
  const results = event.send_results || [];

  const leadHref = `/contacts?q=${encodeURIComponent(parsed.mobile || phones[0] || "")}`;

  return (
    <Box sx={{ minWidth: 0, width: "100%" }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/inbound-email")} sx={{ mb: 1.5 }}>
        Back to inbound email
      </Button>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.5 }}>
            <Typography variant="h5" fontWeight={700} sx={{ minWidth: 0, flex: 1, overflowWrap: "anywhere" }}>
              {event.subject || "(no subject)"}
            </Typography>
            <Chip size="small" label={event.status} color={statusColor(event.status)} sx={{ flexShrink: 0 }} />
          </Stack>
          <Typography variant="body2" color="text.secondary" noWrap>
            {event.mailbox_name ? `${event.mailbox_name} · ` : ""}{formatWhen(event.created_at)}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
          {(phones.length > 0 || parsed.mobile) && (
            <Button startIcon={<SendIcon />} disabled={busy} onClick={resend}>Resend WA</Button>
          )}
          {(event.status === "failed" || event.status === "skipped") && (
            <Button startIcon={<ReplayIcon />} disabled={busy} onClick={recreate}>Recreate</Button>
          )}
          <Button color="error" startIcon={<DeleteIcon />} disabled={busy} onClick={remove}>Delete</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {event.error_message && <Alert severity="warning" sx={{ mb: 2 }}>{event.error_message}</Alert>}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1.4fr) minmax(320px, 1fr)" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Paper variant="outlined" sx={{ p: 2.5, minWidth: 0, overflow: "hidden" }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Email</Typography>
          <DetailRow label="From" value={event.from_address} />
          <DetailRow label="To" value={event.to_address} />
          <DetailRow label="Mailbox" value={event.mailbox_name} />
          <DetailRow label="Received" value={formatWhen(event.created_at)} />
          <DetailRow label="Updated" value={formatWhen(event.updated_at)} />
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" color="text.secondary">Body preview</Typography>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              mt: 0.5,
              maxHeight: 420,
              overflow: "auto",
            }}
          >
            {event.body_preview || "—"}
          </Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, minWidth: 0, overflow: "hidden" }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Parsed</Typography>
          <DetailRow label="Name" value={parsed.name} />
          <DetailRow label="Email" value={parsed.email} />
          <DetailRow label="Mobile" value={parsed.mobile || phones[0]} />
          <DetailRow label="Company" value={parsed.company} />
          <DetailRow label="Product" value={parsed.product} />
          <DetailRow
            label="Lead"
            value={event.lead_id ? (
              <Link component={RouterLink} to={leadHref} underline="hover">
                {event.lead_id}
              </Link>
            ) : null}
          />
          <DetailRow label="Phones" value={phones.filter(Boolean).join(", ")} />
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" color="text.secondary">Send results</Typography>
          {results.length === 0 && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>—</Typography>
          )}
          {results.map((r, idx) => (
            <Typography key={idx} variant="body2" sx={{ mt: 0.5, overflowWrap: "anywhere" }}>
              {r.channel || "whatsapp"} · {r.phone || "—"} · {r.ok ? "ok" : (r.error || "failed")}
            </Typography>
          ))}
        </Paper>
      </Box>
    </Box>
  );
}

function displayValue(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return value;
}

function DetailRow({ label, value }) {
  const shown = displayValue(value);
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "104px minmax(0, 1fr)",
        columnGap: 1.5,
        alignItems: "start",
        mb: 1,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ pt: "1px" }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{ minWidth: 0, overflowWrap: "anywhere", lineHeight: 1.5 }}
      >
        {shown}
      </Typography>
    </Box>
  );
}
